import React, { useState, useEffect, useCallback, useMemo, useLayoutEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { getCrimeDisplayName } from '../../constants/crimes';
import { getStateAbbreviation } from '../../constants/locations';
import { formatNumber, formatDifference, formatRank, formatPercentile } from '../../utils/formatting';
import { getValueColor, getRateColor, getRateDiffColor } from '../../utils/coloring';
import { useChartState } from '../../hooks/charts';
import { resolveRatesChartMenuSelection } from '../../utils/chartMapSync';
import './Charts.css';

const RatesChart = ({ 
  selectedLocation, 
  selectedCrime, 
  selectedDateRange, 
  onChartOptionChange, 
  selectedChartOption, 
  selectedSource,
  compareMode,
  onCompareModeChange,
  onUnifiedCompareModeChange
}) => {
  const [selectedChartState, setSelectedChart] = useState(selectedChartOption);
  const {
    isMobile,
    isMenuOpen,
    setIsMenuOpen,
    isTouching,
    setIsTouching,
    menuRef,
    getDropdownLeftPosition
  } = useChartState();

  const current = selectedLocation?.data?.current;
  const locationType = selectedLocation.info?.type?.charAt(0).toUpperCase() + selectedLocation.info?.type?.slice(1);
  const isNation = selectedLocation.info?.type?.toLowerCase() === 'nation'; 
  const isCounty = selectedLocation.info?.type?.toLowerCase() === 'county';
  const isCity = selectedLocation.info?.type?.toLowerCase() === 'city';
  const isMetro = selectedLocation.info?.type?.toLowerCase() === 'metro';
  const isCountyOrCity = isCounty || isCity;
  
  const stateAbbr = React.useMemo(() => {
    if (isCountyOrCity && selectedLocation?.info?.name?.includes(',')) {
      const stateName = selectedLocation.info.name.split(',')[1]?.trim();
      return getStateAbbreviation(stateName);
    }
    return getStateAbbreviation(selectedLocation);
  }, [isCountyOrCity, selectedLocation]);

  const getComparisonLabel = useCallback(() => {
    if (isMetro) {
      return compareMode.rateType === 'metro' ? 'Metro' : 'National';
    }
    if (isCountyOrCity) {
      if (compareMode.rateType === 'county' || compareMode.rateType === 'city') {
        const peerLocLabel = compareMode.rateType === 'county' ? 'County' : 'City';
        return compareMode.rateScope === 'state'
          ? `${stateAbbr} ${peerLocLabel}`
          : `US ${peerLocLabel}`;
      }
      return compareMode.rateScope === 'state'
        ? `${stateAbbr} State`
        : 'US National';
    }
    return '';
  }, [isMetro, isCountyOrCity, compareMode, stateAbbr]);

  useEffect(() => {
    if (!selectedChartOption) return;

    if (selectedChartOption === 'Crime Rate') {
      setSelectedChart('Crime Rate');
      return;
    }
    
    if (isCountyOrCity || isMetro) {
      if (isMetro && (selectedChartOption.includes('Rank') || selectedChartOption.includes('Percentile'))) {
        setSelectedChart(selectedChartOption);
        return;
      }

      const currentPrefix = getComparisonLabel();
      const hasCorrectPrefix = selectedChartOption.includes(currentPrefix);
      
      if (!hasCorrectPrefix) {
        if (selectedChartOption.includes('vs')) {
          setSelectedChart(`Crime Rate vs ${currentPrefix} Rate`);
        } 
        else if (selectedChartOption.includes('%') && selectedChartOption.includes('Rate Difference')) {
          setSelectedChart(`% ${currentPrefix} Rate Difference`);
        }
        else if (selectedChartOption.includes('Difference')) {
          setSelectedChart(`${currentPrefix} Rate Difference`);
        }
        else if (selectedChartOption.includes('Rank')) {
          setSelectedChart(`${currentPrefix} Rank`);
        }
        else if (selectedChartOption.includes('Percentile')) {
          setSelectedChart(`${currentPrefix} Percentile`);
        }
        else {
          setSelectedChart(`${currentPrefix} ${selectedChartOption.split(' ').slice(-1)[0]}`);
        }
      } else {
        setSelectedChart(selectedChartOption);
      }
    } else {
      setSelectedChart(selectedChartOption);
    }
  }, [selectedChartOption, getComparisonLabel, isCountyOrCity, isMetro]);

  const comparisonLabelStr = getComparisonLabel();
  const chartOptions = useMemo(
    () => [
      'Crime Rate',
      `${(isCountyOrCity || isMetro) ? comparisonLabelStr : 'National'} Rate`,
      `${(isCountyOrCity || isMetro) ? comparisonLabelStr : 'National'} Rate Difference`,
      `% ${(isCountyOrCity || isMetro) ? comparisonLabelStr : 'National'} Rate Difference`,
      `Crime Rate vs ${(isCountyOrCity || isMetro) ? comparisonLabelStr : 'National'} Rate`,
      `${isCountyOrCity ? comparisonLabelStr : locationType} Rank`,
      `${isCountyOrCity ? comparisonLabelStr : locationType} Percentile`,
    ],
    [comparisonLabelStr, isCountyOrCity, isMetro, locationType]
  );

  const chartToUse = useMemo(
    () =>
      resolveRatesChartMenuSelection({
        selectedChart: selectedChartState,
        selectedChartOption,
        chartOptions,
        comparisonLabel: comparisonLabelStr,
        locationType,
        isCountyOrCity,
        isMetro,
      }),
    [
      selectedChartState,
      selectedChartOption,
      chartOptions,
      comparisonLabelStr,
      locationType,
      isCountyOrCity,
      isMetro,
    ]
  );

  useLayoutEffect(() => {
    if (!chartToUse || !onChartOptionChange) return;
    if (chartToUse !== selectedChartOption) {
      onChartOptionChange(chartToUse);
    }
  }, [chartToUse, selectedChartOption, onChartOptionChange]);

  if (selectedLocation?.loading || !selectedLocation?.data?.current || selectedLocation?.info?.name?.startsWith('All ')) {
    return null;
  }

  const hasCrimeData = [
    'all_crime_count', 'violent_crime_count', 'property_crime_count',
    'murder_count', 'rape_count', 'robbery_count', 'aggravated_assault_count',
    'burglary_count', 'larceny_theft_count', 'motor_vehicle_theft_count'
  ].some(field => 
    current[field] !== null && 
    current[field] !== undefined && 
    current[field] !== 'N/A'
  );
  
  if (!hasCrimeData) {
    return <div className="loading">No Data Available</div>;
  }

  const handleTouchStart = () => {
    setIsTouching(true);
  };

  const handleTouchEnd = () => {
    setIsTouching(false);
  };
  
  const getChartOption = (baseOption) => {
    if (baseOption === 'Crime Rate') {
      return baseOption;
    }
    
    if (isCountyOrCity || isMetro) {
      if (isMetro && (baseOption.includes('Rank') || baseOption.includes('Percentile'))) {
        return baseOption;
      }

      const comparisonLabel = getComparisonLabel();
      
      if (baseOption.includes('Rank') || baseOption.includes('Percentile')) {
        return comparisonLabel + baseOption.substring(baseOption.indexOf(' '));
      }
      
      return baseOption.replace(/National/, comparisonLabel);
    }
    
    return baseOption;
  };

  const getTotalLocations = () => {
    if (isCountyOrCity) {
      return compareMode.rateScope === 'state' ? 
        (isCounty ? current.total_counties_state : current.total_cities_state) || current.total_locations : 
        (isCounty ? current.total_counties_national : current.total_cities_national) || current.total_locations;
    }
    
    return current.total_locations || 0;
  };

  const extractNumber = (rank) => {
    if (typeof rank !== 'string') return rank;
    const match = rank.match(/\d+/);
    return match ? parseInt(match[0], 10) : rank;
  };

  const transformData = () => {
    if (selectedSource === 'BJS_NCVS') {
      return [
        {
          name: 'All Crime',
          currentRate: current.all_crime_rate,
        },
        {
          name: 'Violent Crime',
          currentRate: current.violent_crime_rate,
        },
        {
          name: 'Rape',
          currentRate: current.rape_rate, 
        },
        {
          name: 'Robbery',
          currentRate: current.robbery_rate,
        },
        {
          name: 'Aggravated Assault',
          currentRate: current.aggravated_assault_rate,
        },
        {
          name: 'Simple Assault',
          currentRate: current.simple_assault_rate,
        },
        {
          name: 'Property Crime',
          currentRate: current.property_crime_rate,
        },
        {
          name: 'Burglary',
          currentRate: current.burglary_rate,
        },
        {
          name: 'Trespassing',
          currentRate: current.trespassing_rate,
        },
        { 
          name: 'Larceny/Theft',
          currentRate: current.larceny_theft_rate,
        },
        {
          name: 'Motor Vehicle Theft',
          currentRate: current.motor_vehicle_theft_rate,
        }
      ];
    }

    const getComparisonFields = (prefix) => {
      let avgRateField, rankField, rateDiffField, rateDiffPctField, percentileField;

      if (isMetro) {
        rankField = `${prefix}_national_rate_rank`;
        percentileField = `${prefix}_national_rate_percentile`;
        if (compareMode.rateType === 'metro') {
          avgRateField = `${prefix}_all_metros_rate`;
          rateDiffField = `${prefix}_all_metros_rate_diff`;
          rateDiffPctField = `${prefix}_all_metros_rate_diff_pct`;
        } else {
          avgRateField = `${prefix}_national_rate`;
          rateDiffField = `${prefix}_national_rate_diff`;
          rateDiffPctField = `${prefix}_national_rate_diff_pct`;
        }
      } else if (isCountyOrCity) {
        if (compareMode.rateType === 'county' || (compareMode.rateType === 'city' && isCity)) {
          if (compareMode.rateScope === 'state') {
            const locTypePrefix = isCounty ? 'counties' : 'cities';
            rankField = `${prefix}_state_rate_rank`;
            percentileField = `${prefix}_state_rate_percentile`;
            avgRateField = `${prefix}_all_${locTypePrefix}_state_rate`;
            rateDiffField = `${prefix}_all_${locTypePrefix}_state_rate_diff`;
            rateDiffPctField = `${prefix}_all_${locTypePrefix}_state_rate_diff_pct`;
          } else { 
            const locTypePrefix = isCounty ? 'counties' : 'cities';
            rankField = `${prefix}_national_rate_rank`;
            percentileField = `${prefix}_national_rate_percentile`;
            avgRateField = `${prefix}_all_${locTypePrefix}_national_rate`;
            rateDiffField = `${prefix}_all_${locTypePrefix}_national_rate_diff`;
            rateDiffPctField = `${prefix}_all_${locTypePrefix}_national_rate_diff_pct`;
          }
        } else { 
          if (compareMode.rateScope === 'state') {
            rankField = `${prefix}_state_rate_rank`;
            percentileField = `${prefix}_state_rate_percentile`;
            avgRateField = `${prefix}_state_rate`;
            rateDiffField = `${prefix}_state_rate_diff`;
            rateDiffPctField = `${prefix}_state_rate_diff_pct`;
          } else { 
            rankField = `${prefix}_national_rate_rank`;
            percentileField = `${prefix}_national_rate_percentile`;
            avgRateField = `${prefix}_national_rate`;
            rateDiffField = `${prefix}_national_rate_diff`;
            rateDiffPctField = `${prefix}_national_rate_diff_pct`;
          }
        }
      } else {
        rankField = `${prefix}_rate_rank`;
        percentileField = `${prefix}_rate_percentile`;
        avgRateField = `${prefix}_national_rate`;
        rateDiffField = `${prefix}_national_rate_diff`;
        rateDiffPctField = `${prefix}_national_rate_diff_pct`;
      }
      
      return {
        nationalRate: current[avgRateField],
        difference: current[rateDiffField],
        percentDiff: current[rateDiffPctField],
        rank: current[rankField],
        rankValue: extractNumber(current[rankField]),
        percentile: (current[percentileField] > 0 && current[percentileField] < 1) ? 1 : current[percentileField]
      };
    };

    return [
      {
        name: 'All Crime',
        currentRate: current.all_crime_rate,
        ...getComparisonFields('all_crime')
      },
      {
        name: 'Violent Crime',
        currentRate: current.violent_crime_rate,
        ...getComparisonFields('violent_crime')
      },
      {
        name: 'Murder',
        currentRate: current.murder_rate,
        ...getComparisonFields('murder')
      },
      {
        name: 'Rape',
        currentRate: current.rape_rate,
        ...getComparisonFields('rape')
      },
      {
        name: 'Robbery',
        currentRate: current.robbery_rate,
        ...getComparisonFields('robbery')
      },
      {
        name: 'Aggravated Assault',
        currentRate: current.aggravated_assault_rate,
        ...getComparisonFields('aggravated_assault')
      },
      {
        name: 'Property Crime',
        currentRate: current.property_crime_rate,
        ...getComparisonFields('property_crime')
      },
      {
        name: 'Burglary',
        currentRate: current.burglary_rate,
        ...getComparisonFields('burglary')
      },
      {
        name: 'Larceny/Theft',
        currentRate: current.larceny_theft_rate,
        ...getComparisonFields('larceny_theft')
      },
      {
        name: 'Motor Vehicle Theft',
        currentRate: current.motor_vehicle_theft_rate,
        ...getComparisonFields('motor_vehicle_theft')
      }
    ];
  };

  const chartData = transformData();
  
  const getMaxValue = () => {
    switch (chartToUse) {
      case getChartOption('Crime Rate'):
        return Math.max(...chartData.map(item => item.currentRate || 0));
      case getChartOption(`National Rate`):
        return Math.max(...chartData.map(item => item.nationalRate || 0));
      case getChartOption(`National Rate Difference`):
      case getChartOption(`% National Rate Difference`):
        const values = chartData.map(item => 
          chartToUse === getChartOption(`National Rate Difference`) ? 
            item.difference : item.percentDiff
        );
        return Math.max(Math.abs(Math.min(...values)), Math.max(...values));
      case getChartOption(`${locationType} Rank`):
        return getTotalLocations();
      case getChartOption(`${locationType} Percentile`):
        return 100;
      default:
        return Math.max(
          ...chartData.map(item => Math.max(item.currentRate || 0, item.nationalRate || 0))
        );
    }
  };

  const maxValue = getMaxValue();
  
  const getAxisMaxValue = (value) => {
    if (chartToUse === getChartOption(`${locationType} Rank`)) {
      return getTotalLocations();
    } else if (chartToUse === getChartOption(`${locationType} Percentile`)) {
      return 100;
    } else if (chartToUse.includes('%')) {
      return Math.ceil(value / 10) * 10;
    } else {
      const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
      const normalized = value / magnitude;
      const rounded = Math.ceil(normalized / 0.1) * 0.1;
      return rounded * magnitude;
    }
  };
  
  const getTicks = (maxVal) => {
    if (chartToUse === getChartOption(`${locationType} Rank`)) {
      return [1, Math.ceil(getTotalLocations()/4), Math.ceil(getTotalLocations()/2), 
        Math.ceil(getTotalLocations()*3/4), getTotalLocations()];
    } else if (chartToUse === getChartOption(`${locationType} Percentile`)) {
      return [1, 25, 50, 75, 100];
    } else if (chartToUse.includes('%')) {
      if (isMobile && window.innerWidth < 400) {
        return [-maxVal, 0, maxVal];
      }
      return [-maxVal, -maxVal/2, 0, maxVal/2, maxVal];
    } else {
      if (isMobile && window.innerWidth < 400) {
        return [0, maxVal/2, maxVal];
      }
      return [0, maxVal/4, maxVal/2, maxVal*3/4, maxVal];
    }
  };

  const renderTotalBar = (props) => {
    const { x, y, width, height, payload, dataKey } = props;
    const isMainCategory = ['All Crime', 'Violent Crime', 'Property Crime'].includes(payload.name);
    const isRank = chartToUse === getChartOption(`${locationType} Rank`);
    const isPercentile = chartToUse === getChartOption(`${locationType} Percentile`);
    const value = props.dataKey === 'currentRate' ? payload.currentRate : payload.nationalRate;
    
    const barWidth = Math.max(1, width || 0);
    if (!barWidth) return null;

    if (!isRank && !isPercentile) {  
      if (value === null || value === undefined || isNaN(value)) {
        return (
          <g>
            <text
              x={x + 5}
              y={y + height / 2}
              dy="0.35em"
              textAnchor="start"
              fill="var(--color)"
              style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600 }}
            >
              -
            </text>
          </g>
        );
      }
  
      if (value === 0) {
        return (
          <g>
            <text
              x={x + 5}
              y={y + height / 2}
              dy="0.35em"
              textAnchor="start"
              fill="var(--color)"
              style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600 }}
            >
              0
            </text>
          </g>
        );
      }
    }
    
    if (isRank || isPercentile) {
      const value = isRank ? payload.rankValue : payload.percentile;     
      const maxValue = isRank ? getTotalLocations() : 100;
      const isNearMax = value >= (maxValue * 0.9);
      const hasValidValue = value !== null && value !== undefined && !isNaN(value);
      
      return (
        <g>
          <rect
            x={x}
            y={y}
            width={barWidth}
            height={height}
            fill="transparent"
          />
          {hasValidValue && (
            <circle
              cx={x + barWidth}
              cy={y + height/2}
              r={6}
              fill={`var(--navy)`}
              fillOpacity={isMainCategory ? 0.6 : 0.3}
              stroke="black"
            />
          )}
          <text
            x={hasValidValue ? x + barWidth + (isNearMax ? -12 : 12) : x + 5}
            y={y + height/2}
            dy="0.35em"
            textAnchor={isNearMax ? "end" : "start"}
            style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600 }}
          >
            {isRank ? formatRank(payload.rank) : formatPercentile(payload.percentile)}
          </text>
        </g>
      );
    }
    
    const maxAxisValue = getAxisMaxValue(maxValue);
    const relativePercentage = (value / maxAxisValue);
    const isComparison = chartToUse === getChartOption(`Crime Rate vs National Rate`);
    const isTextInside = isComparison ? relativePercentage > (isMobile ? 0.5 : 0.25) : relativePercentage > (isMobile ? 0.25 : 0.12);
    const diff = isComparison && dataKey === 'currentRate' ? payload.currentRate - payload.nationalRate : null;
    const useComparisonStyle = isComparison && dataKey === 'currentRate';
    const isCrimeRate = chartToUse === getChartOption('Crime Rate');
    const isNationalRateNull = payload.nationalRate === null || payload.nationalRate === undefined || isNaN(payload.nationalRate);
    const color = (isNationalRateNull && !isCrimeRate) ? 'silver' : 
                (!isNation && isCrimeRate ? getRateColor(payload.currentRate, payload.nationalRate) :
                useComparisonStyle ? getValueColor(diff) : 'navy');
    const chartAreaStart = isMobile ? 160 : 180;
    
    const textX = isTextInside ? 
      Math.max(chartAreaStart + 50, x + barWidth - 4) : 
      Math.max(chartAreaStart + 4, x + barWidth + 4); 
    
    return (
      <g>
        <rect
          x={x}
          y={y}
          width={barWidth}
          height={height}
          fill={`var(--${color})`}
          fillOpacity={isMainCategory ? 0.6 : 0.3}
          stroke={`var(--${color})`}
          strokeWidth={1}
        />
        <text
          x={textX}
          y={y + height / 2}
          dy="0.35em"
          textAnchor={isTextInside ? "end" : "start"}
          fill={isMainCategory && isTextInside ? 
            ((isNationalRateNull && isComparison) ? 'black' : 
             useComparisonStyle ? `var(--very-light-${color})` : 'var(--light-gray)') 
            : ((isNationalRateNull && isComparison) ? 'black' : `var(--${color})`)}
          style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600 }}
        >
          {formatNumber(value)}
          {isComparison && dataKey === 'currentRate' && (
            <tspan 
              dx={4}
              style={{ fontWeight: 400 }}
            >
              ({formatDifference(payload.difference, false, true)})
            </tspan>
          )}
        </text>
      </g>
    );
  };

  const renderDifferenceBar = (props) => {
    const { x, y, width, height, payload } = props;
    const isMainCategory = ['All Crime', 'Violent Crime', 'Property Crime'].includes(payload.name);
    const isPercentDiff = chartToUse === getChartOption(`% National Rate Difference`);
    const value = isPercentDiff ? payload.percentDiff : payload.difference;
    const isZero = parseFloat(value) === 0;
    
    if (isZero) {
      return (
        <g>
          <text
            x={x + 5}
            y={y + height / 2}
            dy="0.35em"
            textAnchor="start"
            fill={isPercentDiff ? "black" : "green"}
            style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600 }}
          >
            {isPercentDiff ? '0%' : '0x'}
          </text>
        </g>
      );
    }

    if (value === null || value === undefined || isNaN(value)) {
      return (
        <g>
          <text
            x={x + 5}
            y={y + height / 2}
            dy="0.35em"
            textAnchor="start"
            fill="var(--color)"
            style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600 }}
          >
            -
          </text>
        </g>
      );
    }

    const isNegative = value < 0;
    const color = isPercentDiff ? getValueColor(value) : getRateDiffColor(value);
    
    const barWidth = Math.abs(width || 0); 
    if (!barWidth) return null; 
    const barX = isNegative ? x + width : x;
    
    const maxAxisValue = getAxisMaxValue(maxValue);
    const relativePercentage = Math.abs(value / maxAxisValue);
    const isTextInside = relativePercentage > (isMobile ? 0.5 : 0.2);
    
    const textX = isNegative ? 
      (isTextInside ? barX + 4 : barX - 4) : 
      (isTextInside ? x + barWidth - 4 : x + barWidth + 4);
    
    const formattedValue = isPercentDiff ? formatDifference(value, true) : `${formatDifference(value, false, true)}`;
    
    return (
      <g>
        <rect
          x={barX}
          y={y}
          width={barWidth}
          height={height}
          fill={`var(--${color})`}
          fillOpacity={isMainCategory ? 0.6 : 0.3}
          stroke={`var(--${color})`}
          strokeWidth={1}
        />
        <text
          x={textX}
          y={y + height / 2}
          dy="0.35em"
          textAnchor={isNegative ? 
            (isTextInside ? "start" : "end") : 
            (isTextInside ? "end" : "start")}
          fill={isMainCategory && isTextInside ? 
            `var(--very-light-${color})` : 
            `var(--${color})`}
          style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600 }}
        >
          {formattedValue}
        </text>
      </g>
    );
  };

  const renderCustomLegend = () => {
    const renderLegendBar = () => {
      if (isNation) {
        return (
          <div style={{ 
            width: '80px', 
            height: '12px',
            background: 'linear-gradient(to right, rgba(26, 44, 78, 0.3), rgba(26, 44, 78, 0.6))',
            border: '1px solid var(--navy)'
          }} />
        );
      }

      switch (chartToUse) {
        case getChartOption(`Crime Rate`):
        case getChartOption(`National Rate Difference`):
        case getChartOption(`% National Rate Difference`):
        case getChartOption(`Crime Rate vs National Rate`):
          return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ 
                width: isMobile ? '30px' : '40px', 
                height: '12px',
                background: 'linear-gradient(to right, rgba(0, 128, 0, 0.6), rgba(0, 128, 0, 0.3))',
                borderLeft: '1px solid var(--green)',
                borderTop: '1px solid var(--green)',
                borderBottom: '1px solid var(--green)'
              }} />
              <div style={{ 
                width: isMobile ? '30px' : '40px', 
                height: '12px',
                background: 'linear-gradient(to right, rgba(255, 0, 0, 0.3), rgba(255, 0, 0, 0.6))',
                borderRight: '1px solid var(--red)',
                borderTop: '1px solid var(--red)',
                borderBottom: '1px solid var(--red)'
              }} />
            </div>
          );

        case getChartOption(`${locationType} Rank`):
        case getChartOption(`${locationType} Percentile`):
          return (
            <div style={{ 
              width: '12px', 
              height: '12px',
              background: 'linear-gradient(to right, rgba(26, 44, 78, 0.3), rgba(26, 44, 78, 0.6))',
              border: '1px solid var(--navy)',
              borderRadius: '50%',
            }} />
          );

        default:
          return (
            <div style={{ 
              width: isMobile ? '60px' : '80px', 
              height: '12px',
              background: 'linear-gradient(to right, rgba(26, 44, 78, 0.3), rgba(26, 44, 78, 0.6))',
              border: '1px solid var(--navy)'
            }} />
          );
      }
    };

    const getDisplayTitle = () => {
      if (chartToUse === getChartOption(`Crime Rate vs National Rate`)) {
        return 'Crime Rate';
      }
      if (selectedSource === 'BJS_NCVS' && chartToUse === 'Crime Rate') {
        return 'Victimization Rate';
      }
      return chartToUse;
    };

    return (
      <div style={{ 
        display: 'flex', 
        gap: '20px', 
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 0 10px',
        position: 'relative'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {renderLegendBar()}
          <div 
            ref={menuRef}
            style={{ 
              display: 'flex', 
              alignItems: 'center',
              cursor: isNation ? 'default' : 'pointer',
              gap: '4px',
              position: 'relative'
            }}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <span style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600 }}>{getDisplayTitle()}</span>

            {!isNation && (
              <>
                <ChevronDown className="chart-legend-chevron" style={{ width: '16px', height: '16px' }} />
              
                {isMenuOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: getDropdownLeftPosition(chartToUse),
                    backgroundColor: 'white',
                    border: '1px solid var(--silver)',
                    borderRadius: '4px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    zIndex: 1000,
                    minWidth: 'max-content',
                    marginTop: '4px',
                    whiteSpace: 'nowrap'
                  }}>
                    {chartOptions.map((option, index) => (
                      <div
                        key={option}
                        style={{
                          padding: '8px 12px',
                          cursor: chartToUse === option ? 'not-allowed' : 'pointer',
                          fontSize: isMobile ? '11px' : '12px',
                          fontWeight: 600,
                          textAlign: 'left',
                          backgroundColor: chartToUse === option ? 'var(--light-gray)' : 'transparent',
                          borderBottom: index < chartOptions.length - 1 ? '1px solid var(--border-color)' : 'none',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          position: 'relative', 
                          boxSizing: 'border-box',
                          height: '31px'
                          }}
                        onMouseEnter={(e) => {
                          if (chartToUse !== option) {
                            e.currentTarget.style.backgroundColor = 'var(--silver)';
                          }
                        }}
                         onMouseLeave={(e) => {
                          if (chartToUse !== option) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                        onClick={(e) => {
                          if (chartToUse === option) {
                            e.stopPropagation();
                            return;
                          }
                          e.stopPropagation();
                          setSelectedChart(option);
                          onChartOptionChange(option);
                          setIsMenuOpen(false);
                        }}
                      >
                        <span style={{ opacity: chartToUse === option ? 0.5 : 1 }}>
                          {option}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        {chartToUse === getChartOption(`Crime Rate vs National Rate`) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ 
              width: isMobile ? '60px' : '80px', 
              height: '12px',
              background: 'linear-gradient(to right, rgba(26, 44, 78, 0.3), rgba(26, 44, 78, 0.6))',
              border: '1px solid var(--navy)'
            }} />
            <span style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600 }}>
              {isCountyOrCity || isMetro ? getComparisonLabel() : 'National'} Rate
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`chart-container ${isMenuOpen ? 'active' : ''}`}>
      {renderCustomLegend()}
      <div 
        className="chart-content" 
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        <ResponsiveContainer>
          <BarChart
            key={chartToUse}
            data={chartData}
            layout="vertical"
            margin={{ top: 0, right: isMobile ? 30 : 40, left: 20, bottom: 0 }}
          >
            <XAxis 
              type="number"
              tickFormatter={(value) => {
                if (chartToUse === getChartOption(`${locationType} Percentile`)) {
                  return formatPercentile(value);
                } else if (chartToUse === getChartOption(`${locationType} Rank`)) {
                  return formatRank(value);
                } else if (chartToUse === getChartOption(`National Rate Difference`)) {
                  return value === 0 ? '0' : formatDifference(value, false, true);
                } else if (chartToUse === getChartOption(`% National Rate Difference`)) {
                  return value === 0 ? '0' : formatDifference(value, true);
                } else {
                  return value === 0 ? '0' : formatNumber(value);
                }
              }}
              domain={
                chartToUse === getChartOption(`${locationType} Rank`) ? 
                  [1, current.total_locations] :
                chartToUse === getChartOption(`${locationType} Percentile`) ?
                  [1, 100] :
                chartToUse.includes('%') ? 
                  [-getAxisMaxValue(maxValue), getAxisMaxValue(maxValue)] : 
                  [0, getAxisMaxValue(maxValue)]
              }
              axisLine={{ stroke: 'var(--border-color)' }}
              tickLine={{ stroke: 'var(--border-color)' }}
              ticks={getTicks(getAxisMaxValue(maxValue))}
              tick={{ fontSize: isMobile ? 12 : 14, fontWeight: 400, fill: 'black' }}
              interval="preserveStartEnd"
              allowDecimals={false}
              height={isMobile ? 20 : 30}
            />
            <YAxis 
              type="category" 
              dataKey="name"
              width={isMobile ? 140 : 160}
              axisLine={{ stroke: 'var(--border-color)' }}
              tickLine={false} 
              interval={0}
              tick={(props) => {
                const { y, payload, index, height, visibleTicksCount } = props;
                let indent = 12;
                if (['Violent Crime', 'Property Crime'].includes(payload.value)) {
                  indent = 24;
                } else if (['Murder', 'Rape', 'Robbery', 'Aggravated Assault', 'Burglary', 'Larceny/Theft', 'Motor Vehicle Theft', 'Simple Assault', 'Trespassing'].includes(payload.value)) {
                  indent = 36;
                }

                const spacing = height / visibleTicksCount;
                const isSelected = payload.value === getCrimeDisplayName(selectedCrime, selectedSource) || 
                                (selectedCrime === 'all_crime' && payload.value === 'All Crime');

                return (
                  <g>
                    {['All Crime', 'Violent Crime', 'Property Crime'].includes(payload.value) && (
                      <rect
                        x={0}
                        y={y - spacing/2}
                        width={isMobile ? "calc(100% - 30px)" : "calc(100% - 40px)"}
                        height={spacing}
                        fill="var(--light-gray)"
                        fillOpacity={1}
                      />
                    )}
                    {isSelected && (
                      <rect
                        x={0}
                        y={y - spacing/2}
                        width={isMobile ? "calc(100% - 30px)" : "calc(100% - 40px)"}
                        height={spacing}
                        fill="#fef9c3"
                        fillOpacity={0.9}
                      />
                    )}
                    {index === 0 && (
                      <>
                        <line 
                          x1={0} 
                          y1={y - spacing/2}
                          x2={isMobile ? 160 : 180} 
                          y2={y - spacing/2}
                          stroke="var(--border-color)" 
                          strokeWidth={4}
                        />
                        <line 
                          x1={0} 
                          y1={y + spacing/2}
                          x2={isMobile ? 160 : 180} 
                          y2={y + spacing/2}
                          stroke="var(--border-color)" 
                          strokeWidth={2}
                        />
                      </>
                    )}
                    {index > 0 && (
                      <line 
                        x1={0} 
                        y1={y + spacing/2} 
                        x2={isMobile ? 160 : 180} 
                        y2={y + spacing/2} 
                        stroke="var(--border-color)" 
                        strokeWidth={1}
                      />
                    )}
                    {index === chartData.length - 1 && (
                      <line 
                        x1={0} 
                        y1={y + spacing/2} 
                        x2={isMobile ? 160 : 180} 
                        y2={y + spacing/2} 
                        stroke="var(--border-color)" 
                        strokeWidth={1}
                      />
                    )}
                    <text 
                      x={indent} 
                      y={y} 
                      dy={4} 
                      textAnchor="start" 
                      style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: 600 }}
                    >
                      {payload.value}
                    </text>
                  </g>
                );
              }}
            />
            <CartesianGrid 
              stroke="var(--border-color)"
              strokeDasharray="none"
              horizontal={true}
              horizontalCoordinatesGenerator={({ yAxis }) => {
                return yAxis.scale.domain().map(y => yAxis.scale(y));
              }}
            />
            <line 
              x1={isMobile ? 160 : 180} 
              y1={0} 
              x2="100%" 
              y2={0} 
              stroke="var(--border-color)" 
              strokeWidth={4}
              style={{ transform: `translateX(-${isMobile ? 30 : 40}px)` }}
            />
            {chartToUse === getChartOption(`National Rate Difference`) && (
              <ReferenceLine
                x={1}
                stroke="var(--silver)"
                strokeDasharray="6 6"
                strokeWidth={2}
              />
            )}
            <Tooltip 
              cursor={{
                fill: 'rgba(0, 0, 0, 0.1)',
                x: -160,
                width: "calc(100% + 160px)"
              }}
              active={isMobile ? isTouching : undefined}
              wrapperStyle={isMobile ? { transform: 'translateX(140px)' } : {}}
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  const rateColor = getRateColor(data.currentRate, data.nationalRate);
                  const rateDiffColor = getRateDiffColor(data.difference);
                  const ratePercentDiffColor = getValueColor(data.percentDiff);
                  
                  const getRowStyle = (type) => ({
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    gap: '24px',
                    backgroundColor: (
                      (!isNation && chartToUse === getChartOption('Crime Rate') && type === 'Crime Rate') ||
                      (chartToUse === getChartOption(`National Rate`) && type === 'National Rate') ||
                      (chartToUse === getChartOption(`National Rate Difference`) && type === 'National Rate Difference') ||
                      (chartToUse === getChartOption(`% National Rate Difference`) && type === '% National Rate Difference') ||
                      (chartToUse === getChartOption(`${locationType} Rank`) && type === 'Rank') ||
                      (chartToUse === getChartOption(`${locationType} Percentile`) && type === 'Percentile') ||
                      (chartToUse === getChartOption(`Crime Rate vs National Rate`) && 
                        (type === 'Crime Rate' || type === 'National Rate' || type === 'National Rate Difference'))
                    ) ? '#fef9c3' : 'transparent',
                    padding: '2px 4px',
                    margin: '0 -4px'
                  });
                  
                  return (
                    <div className="custom-tooltip" style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      padding: '12px',
                      border: '1px solid var(--silver)',
                      borderRadius: '4px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                      <div style={{ 
                        margin: '0 0 4px 0',
                        fontWeight: 600,
                        fontSize: isMobile ? '13px' : '14px',
                        color: 'black',
                        textAlign: 'left',
                        paddingBottom: '2px',
                      }}>
                        {isCountyOrCity ? ((selectedLocation.info.name.split(',')[0]) + ', ' + stateAbbr) : selectedLocation.info.name}
                      </div>
                      <div style={{ 
                        margin: '0 0 4px 0',
                        fontWeight: 400,
                        fontSize: isMobile ? '13px' : '14px',
                        textAlign: 'left',
                        borderBottom: '1px solid var(--silver)',
                        paddingBottom: '8px'
                      }}>
                        {selectedDateRange}
                      </div>
                      <div style={{ fontSize: isMobile ? '11px' : '12px', display: 'grid', gap: '4px', paddingTop: '6px' }}>
                        <div style={getRowStyle('Crime Rate')}>
                          <span style={{ fontWeight: 600 }}>
                            {data.name === 'All Crime' 
                              ? (selectedSource === 'BJS_NCVS' ? 'Victimizations' : 'Crime') 
                              : data.name} Rate:
                          </span>
                          <span style={{ color: `var(--${rateColor})`}}>{formatNumber(data.currentRate)}</span>
                        </div>
                        {!isNation && (
                          <>
                            <div style={getRowStyle('National Rate')}>
                              <span style={{ fontWeight: 600 }}>
                                {isCountyOrCity || isMetro ? getComparisonLabel() : 'National'} Rate:
                              </span>
                              <span>{formatNumber(data.nationalRate)}</span>
                            </div>
                            <div style={getRowStyle('National Rate Difference')}>
                              <span style={{ fontWeight: 600 }}>
                                {isCountyOrCity || isMetro ? getComparisonLabel() : 'National'} Rate Difference:
                              </span>
                              <span style={{ color: `var(--${rateDiffColor})`, fontWeight: 600  }}>
                                {formatDifference(data.difference, false, true)}
                              </span>
                            </div>
                            <div style={getRowStyle('% National Rate Difference')}>
                              <span style={{ fontWeight: 600 }}>
                                % {isCountyOrCity || isMetro ? getComparisonLabel() : 'National'} Rate Difference:
                              </span>
                              <span style={{ color: `var(--${ratePercentDiffColor})` }}>
                                {formatDifference(data.percentDiff, true)}
                              </span>
                            </div>
                            <div style={getRowStyle('Rank')}>
                              <span style={{ fontWeight: 600 }}>
                                {isCountyOrCity ? getComparisonLabel() : locationType} Rank:
                              </span>
                              <span>{formatRank(data.rank)}</span>
                            </div>
                            <div style={getRowStyle('Percentile')}>
                              <span style={{ fontWeight: 600 }}>
                                {isCountyOrCity ? getComparisonLabel() : locationType} Percentile:
                              </span>
                              <span>{formatPercentile(data.percentile)}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                }
                return null;
              }}
            />
            {chartToUse === getChartOption(`Crime Rate vs National Rate`) ? (
              <>
                <Bar dataKey="currentRate" shape={renderTotalBar} />
                <Bar dataKey="nationalRate" shape={renderTotalBar} />
              </>
            ) : (
              <Bar
                dataKey={
                  chartToUse === getChartOption('Crime Rate') ? 'currentRate' :
                  chartToUse === getChartOption('National Rate') ? 'nationalRate' :
                  chartToUse === getChartOption(`${locationType} Rank`) ? 'rankValue' :
                  chartToUse === getChartOption(`${locationType} Percentile`) ? 'percentile' :
                  chartToUse === getChartOption(`National Rate Difference`) ? 'difference' : 'percentDiff'
                }
                shape={chartToUse.includes('Difference') ? renderDifferenceBar : renderTotalBar}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default RatesChart;